// Camera: a Wekinator-style 2D controller. A tracked point in the webcam
// frame drives two layers -- a discrete region grid (the study-able one) and
// a latent continuous mapping, off by default.
//
// The mirrored video with the grid and the point drawn over it is not
// decoration: it is the visibility principle as UI. If the person cannot see
// what the system sees they cannot form a model of why a sound fired, and the
// interface stops being a controller and becomes a slot machine.

import { SOUNDS } from '../engine/presets.js'
import { createTracker } from './camera-tracker.js'
import './camera.css'

export const meta = {
  id: 'camera',
  label: 'Camera',
  order: 30,
  paramGroup: 'camera',
  studyCondition: true,
  blurb: 'move a hand through the grid; entering a region plays its sound',
}

// The one-pole coefficient in the params is quoted per frame at this rate, and
// rescaled to the real frame interval below so that a 60 fps phone and a
// 15 fps laptop get the same lag rather than the same arithmetic.
const NOMINAL_FRAME_MS = 1000 / 30

// Continuous layer: 20 Hz ceiling, and a change floor per target, because
// every call lands in the command log and an unthrottled stream would bury
// the triggers the study is actually about.
const CONTINUOUS_MIN_MS = 50
const MOD_INDEX_EPS = 0.5
const TEMPO_EPS = 1
const MOD_INDEX_RANGE = [0, 50]
const TEMPO_RANGE = [60, 160]

const TRACK_COLOURS = { kick: '--kick', snare: '--snare', hat: '--hat', tone: '--tone' }

// Region -> sound, in READING ORDER of the displayed (mirrored, if mirroring
// is on) frame: index 0 is top-left as the person sees it, then left to right,
// then down. With more regions than sounds the mapping cycles, so a 3x3 grid
// is kick snare hat tone / kick snare hat tone / kick. Stable and documented
// so a logged `region` means the same thing across sessions.
export function soundForRegion(index) {
  return SOUNDS[index % SOUNDS.length]
}

// The canonical region for a sound. With a cycled grid a sound owns several;
// the cue highlights the first and `study.geometry` reports the same one, so
// the cue and the logged region always agree.
export function regionForSound(sound, divisions) {
  const i = SOUNDS.indexOf(sound)
  return i >= 0 && i < divisions * divisions ? i : -1
}

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v)
const quantise = (v, step) => Math.round(v / step) * step

export function mount({ api, root, params }) {
  const cfg = () => params.group('camera')

  root.classList.add('camera')
  root.innerHTML = `
    <div class="cam-stage">
      <video class="cam-video" playsinline muted></video>
      <canvas class="cam-overlay"></canvas>
      <div class="cam-gate">
        <button class="cam-enable" type="button">enable camera</button>
        <p class="cam-note">Nothing is recorded or uploaded; the video never leaves this page.</p>
      </div>
    </div>
    <div class="cam-bar">
      <span class="cam-status" role="status"></span>
      <span class="spacer"></span>
      <button class="cam-continuous" type="button" aria-pressed="false">continuous off</button>
    </div>
  `

  const stage = root.querySelector('.cam-stage')
  const video = root.querySelector('.cam-video')
  const canvas = root.querySelector('.cam-overlay')
  const gate = root.querySelector('.cam-gate')
  const enableBtn = root.querySelector('.cam-enable')
  const noteEl = root.querySelector('.cam-note')
  const statusEl = root.querySelector('.cam-status')
  const contBtn = root.querySelector('.cam-continuous')
  const ctx = canvas.getContext('2d')

  let stream = null
  let tracker = null
  let unsubStatus = null
  let continuous = false          // latent, not central: off until asked for
  let cuedSound = null
  let lastSound = null            // the continuous layer's default target

  // Smoothed point in VIDEO coordinates, plus the display-space copy the
  // overlay and the region test both use.
  let smooth = null
  let lastFrameT = 0
  let point = null                // { x, y, t, confidence } in DISPLAY coords

  // --- dwell state machine --------------------------------------------------
  //
  // One object, reset whenever the occupied region changes (including to
  // "none", which is what a tracking dropout or a hand leaving the frame
  // looks like):
  //
  //   null                      the point is outside every region / lost
  //   { region, entryT, fired } the point has been continuously inside
  //                             `region` since frame time `entryT`
  //
  // A frame inside `region` fires once `t - entryT >= dwellMs` and then sets
  // `fired`, which is the no-retrigger latch: staying put cannot fire again
  // because `fired` stays true, and the only way to clear it is to occupy a
  // different region (or none), which replaces the object entirely. So
  // "leave and re-enter" is the sole path to a second trigger, and dwellMs=0
  // collapses to "fire on the first frame inside", entryT === t.
  //
  // Everything here is measured on FRAME timestamps, never on
  // performance.now() at callback time -- see camera-tracker.js.
  let dwell = null

  const flash = new Map()         // region index -> frame time of its last trigger

  function setStatus(text, kind = '') {
    statusEl.textContent = text
    statusEl.dataset.kind = kind
  }

  function showGate(html) {
    gate.hidden = false
    noteEl.innerHTML = html
  }

  // --- camera ---------------------------------------------------------------
  // Never on mount. A camera that turns itself on when a tab loads is the
  // thing people are right to be afraid of.
  async function enableCamera() {
    // Same gesture unlocks audio, so the first region entry makes a sound.
    api.unlock()

    if (!window.isSecureContext) {
      showGate('Camera access needs HTTPS or localhost. This page is on an insecure origin, so the browser will not offer the prompt.')
      enableBtn.hidden = true
      return
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      showGate('This browser has no camera API. Try Safari on iOS or Chrome on Android/desktop.')
      enableBtn.hidden = true
      return
    }

    enableBtn.disabled = true
    enableBtn.textContent = 'starting…'
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      })
      video.srcObject = stream
      await video.play()
    } catch (err) {
      enableBtn.disabled = false
      enableBtn.textContent = 'try again'
      const name = err?.name ?? ''
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        showGate('Camera permission was refused. Allow it in the address-bar camera icon, then try again.')
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        showGate('No camera found. The other interfaces work without one.')
        enableBtn.hidden = true
      } else if (name === 'NotReadableError' || name === 'AbortError') {
        showGate('The camera is busy in another app or tab. Close that and try again.')
      } else {
        showGate(`Could not start the camera (${name || 'unknown error'}).`)
      }
      setStatus('camera off', 'error')
      return
    }

    gate.hidden = true
    resize()
    await startTracking()
  }

  async function startTracking() {
    tracker = createTracker({ backend: 'auto', video })
    unsubStatus = tracker.onStatus((s) => {
      // Which backend is live changes what the interface can do, so say so.
      setStatus(s.message || s.state, s.state === 'error' ? 'error' : '')
    })
    tracker.onPoint(onPoint)
    tracker.onLost(onLost)
    try {
      await tracker.start()
    } catch (err) {
      setStatus(`tracking failed: ${err?.message ?? err}`, 'error')
    }
  }

  // --- the frame path -------------------------------------------------------

  function onPoint(p) {
    const { smoothing, mirror, divisions, dwellMs } = cfg()

    // One-pole filter, as the spec asks. It buys a steady overlay and costs
    // lag, and that lag is a measurement confound: it is added to every
    // movement time, it grows with `smoothing`, and it is not a constant
    // offset because it depends on approach speed. Report the smoothing
    // setting with any movement-time number, or hold it fixed across
    // conditions.
    const dt = lastFrameT ? Math.max(1, p.t - lastFrameT) : NOMINAL_FRAME_MS
    lastFrameT = p.t
    const pole = smoothing > 0 ? Math.pow(smoothing, dt / NOMINAL_FRAME_MS) : 0
    if (!smooth) smooth = { x: p.x, y: p.y }
    smooth = {
      x: pole * smooth.x + (1 - pole) * p.x,
      y: pole * smooth.y + (1 - pole) * p.y,
    }

    // Regions are defined in what the person SEES, so a cue pointing at the
    // top-left square means the top-left square on screen.
    const dx = mirror ? 1 - smooth.x : smooth.x
    point = { x: dx, y: smooth.y, t: p.t, confidence: p.confidence }

    const col = clamp(Math.floor(dx * divisions), 0, divisions - 1)
    const row = clamp(Math.floor(smooth.y * divisions), 0, divisions - 1)
    updateDwell(row * divisions + col, p.t, dwellMs)

    if (continuous) updateContinuous(dx, smooth.y, p.t)
    draw()
  }

  function onLost(t) {
    point = null
    smooth = null
    lastFrameT = t
    updateDwell(null, t, 0)
    draw()
  }

  function updateDwell(region, t, dwellMs) {
    if (region === null) { dwell = null; return }
    if (!dwell || dwell.region !== region) {
      dwell = { region, entryT: t, fired: false }
    }
    if (dwell.fired || t - dwell.entryT < dwellMs) return
    dwell.fired = true
    fire(region, dwell.entryT, t - dwell.entryT, dwellMs)
  }

  function fire(region, entryT, actualDwellMs, targetDwellMs) {
    const sound = soundForRegion(region)
    lastSound = sound
    flash.set(region, entryT)
    // `t` is the moment of ENTRY, not the moment dwell completed: entry is
    // the end of the aimed movement and is what makes camera comparable with
    // a pad press. Dwell is carried alongside rather than folded in, because
    // the spec's open question -- whether dwell time is doing all the work --
    // can only be answered if the analysis can see the two apart. If the
    // answer turns out to be yes, `entryT + dwellMs` reconstructs the
    // completion time exactly.
    api.trigger(sound, {
      t: entryT,
      entryT,
      dwellMs: actualDwellMs,
      dwellTargetMs: targetDwellMs,
      region,
    })
  }

  // --- continuous layer -----------------------------------------------------

  let lastContinuousT = 0
  let lastMod = null
  let lastTempo = null

  function updateContinuous(x, y, t) {
    if (t - lastContinuousT < CONTINUOUS_MIN_MS) return
    lastContinuousT = t

    const sound = cuedSound ?? lastSound ?? 'tone'
    const mod = quantise(MOD_INDEX_RANGE[0] + x * (MOD_INDEX_RANGE[1] - MOD_INDEX_RANGE[0]), MOD_INDEX_EPS)
    // Screen y grows downward; up should mean faster.
    const tempo = Math.round(TEMPO_RANGE[1] - y * (TEMPO_RANGE[1] - TEMPO_RANGE[0]))

    if (lastMod === null || Math.abs(mod - lastMod) >= MOD_INDEX_EPS) {
      lastMod = mod
      api.setParam(sound, 'modulationIndex', mod)
    }
    if (lastTempo === null || Math.abs(tempo - lastTempo) >= TEMPO_EPS) {
      lastTempo = tempo
      api.setTempo(tempo)
    }
  }

  contBtn.addEventListener('click', () => {
    continuous = !continuous
    contBtn.setAttribute('aria-pressed', String(continuous))
    contBtn.textContent = continuous ? 'continuous on' : 'continuous off'
    lastMod = null
    lastTempo = null
  })

  // --- overlay --------------------------------------------------------------

  function cssVar(name) {
    return getComputedStyle(root).getPropertyValue(name).trim() || '#888'
  }

  function resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const w = stage.clientWidth
    const h = stage.clientHeight
    if (!w || !h) return
    canvas.width = Math.round(w * dpr)
    canvas.height = Math.round(h * dpr)
    canvas.style.width = `${w}px`
    canvas.style.height = `${h}px`
    ctx?.setTransform(dpr, 0, 0, dpr, 0, 0)
    draw()
  }

  function draw() {
    if (!ctx) return
    // The context is scaled by dpr, so everything below is in CSS pixels.
    const w = stage.clientWidth
    const h = stage.clientHeight
    if (!w || !h) return
    ctx.clearRect(0, 0, w, h)
    if (!cfg().showOverlay) return

    const { divisions } = cfg()
    const n = divisions * divisions
    const cw = w / divisions
    const ch = h / divisions
    const now = lastFrameT

    ctx.font = '600 13px ui-sans-serif, system-ui, sans-serif'
    ctx.textBaseline = 'top'
    for (let i = 0; i < n; i++) {
      const cx = (i % divisions) * cw
      const cy = Math.floor(i / divisions) * ch
      const sound = soundForRegion(i)
      const colour = cssVar(TRACK_COLOURS[sound])

      // Recent trigger, cue and plain grid, in that order of prominence.
      const since = now - (flash.get(i) ?? -Infinity)
      if (since >= 0 && since < 220) {
        ctx.globalAlpha = 0.45 * (1 - since / 220)
        ctx.fillStyle = colour
        ctx.fillRect(cx, cy, cw, ch)
        ctx.globalAlpha = 1
      }
      const cued = cuedSound && regionForSound(cuedSound, divisions) === i
      if (cued) {
        ctx.globalAlpha = 0.18
        ctx.fillStyle = colour
        ctx.fillRect(cx, cy, cw, ch)
        ctx.globalAlpha = 1
      }
      ctx.strokeStyle = cued ? colour : 'rgba(255,255,255,0.28)'
      ctx.lineWidth = cued ? 3 : 1
      ctx.strokeRect(cx + 0.5, cy + 0.5, cw - 1, ch - 1)
      ctx.fillStyle = colour
      ctx.fillText(sound, cx + 8, cy + 6)
    }

    if (!point) return
    const px = point.x * w
    const py = point.y * h
    // Dwell progress as a ring: the person can see the wait, not just feel it.
    const { dwellMs } = cfg()
    if (dwell && !dwell.fired && dwellMs > 0) {
      const frac = clamp((now - dwell.entryT) / dwellMs, 0, 1)
      ctx.strokeStyle = cssVar('--accent')
      ctx.lineWidth = 3
      ctx.beginPath()
      ctx.arc(px, py, 18, -Math.PI / 2, -Math.PI / 2 + frac * Math.PI * 2)
      ctx.stroke()
    }
    ctx.fillStyle = cssVar('--accent')
    ctx.globalAlpha = 0.35 + 0.65 * clamp(point.confidence, 0, 1)
    ctx.beginPath()
    ctx.arc(px, py, 9, 0, Math.PI * 2)
    ctx.fill()
    ctx.globalAlpha = 1
  }

  // --- wiring ---------------------------------------------------------------

  enableBtn.addEventListener('click', enableCamera)

  const applyMirror = () => video.classList.toggle('mirrored', !!cfg().mirror)
  const unsubParams = params.onGroupChange('camera', () => {
    applyMirror()
    // Changing the grid or the dwell mid-dwell would fire a region the person
    // never entered.
    dwell = null
    draw()
  })
  applyMirror()

  const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(resize) : null
  ro?.observe(stage)
  window.addEventListener('resize', resize)
  resize()
  setStatus('camera off')

  return {
    unmount() {
      tracker?.stop()
      unsubStatus?.()
      unsubParams()
      ro?.disconnect()
      window.removeEventListener('resize', resize)
      // A leaked camera light is unacceptable: kill every track, then drop the
      // element's reference to the stream so nothing can restart it.
      for (const track of stream?.getTracks() ?? []) track.stop()
      stream = null
      video.pause()
      video.srcObject = null
      root.classList.remove('camera')
      root.innerHTML = ''
    },

    study: {
      cue(sound) { cuedSound = sound; draw() },
      clearCue() { cuedSound = null; draw() },
      geometry(sound) { return { region: regionForSound(sound, cfg().divisions) } },
    },
  }
}
